import React, { useState } from 'react';
import CoordinateChallenge from '../../components/chess/CoordinateChallenge';
import styles from './TrainingPage.module.css';
import { Link } from 'react-router-dom';
import { SuperMemoGrade } from '../../types/learning';
import MissionDebugProvider from 'components/debug/MissionDebugProvider';
import MissionDebugWrapper from 'components/debug/MissionDebugWrapper';

interface TrainingStats {
  score: number;
  accuracy: number;
  completed: boolean;
}

/**
 * TrainingPage - Main page for coordinate training
 * 
 * Provides spy-themed UI wrapper for the coordinate challenge.
 */
const TrainingPage: React.FC = () => {
  const [difficulty, setDifficulty] = useState(1);
  const [trainingStats, setTrainingStats] = useState<TrainingStats>({
    score: 0,
    accuracy: 0,
    completed: false,
  });

  /**
   * Handle when the challenge is completed
   * @param success Whether the user passed the challenge
   * @param score User's final score
   * @param total Total available points
   */
  const handleChallengeComplete = (success: boolean, score: number, total: number) => {
    // Calculate accuracy as a percentage
    const accuracy = Math.round((score / total) * 100);
    
    setTrainingStats({
      score,
      accuracy,
      completed: true
    });
    
    // Auto-increase difficulty if accuracy is high enough
    if (accuracy > 0.8 && difficulty < 5) {
      setDifficulty(prev => prev + 1);
    }
  };

  // Helper to map SuperMemoGrade to TrainingStats
  const handleChallengeGrade = (grade: SuperMemoGrade) => {
    // For demo: treat grade as score, accuracy as percent
    setTrainingStats({
      score: grade,
      accuracy: Math.round((grade / 5) * 100),
      completed: true
    });
    // Auto-increase difficulty if accuracy is high enough
    if (grade >= 4 && difficulty < 5) {
      setDifficulty(prev => prev + 1);
    }
  };

  const handleRestartChallenge = () => {
    setTrainingStats({
      score: 0,
      accuracy: 0,
      completed: false,
    });
  };

  return (
    <div className={styles.pageContainer}>
      <header className={styles.header}>
        <h1 className={styles.title}>Training Operations</h1>
        <p className={styles.tagline}>Agent, analyze these coordinates for vital intel!</p>
      </header>

      <nav className={styles.navigation}>
        <Link to="/training/daily" className={styles.navLink}>Access Daily Briefing</Link>
      </nav>

      <main className={styles.main}>
        <div className={styles.missionContainer}>
          {trainingStats.completed ? (
            <div className={styles.missionComplete}>
              <h2 className={styles.missionHeader}>Mission Complete</h2>
              
              <div className={styles.missionStats}>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Final Score:</span>
                  <span className={styles.statValue}>{trainingStats.score}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Accuracy:</span>
                  <span className={styles.statValue}>{trainingStats.accuracy}%</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Agent Level:</span>
                  <span className={styles.statValue}>
                    {difficulty === 1 ? 'Rookie' : 
                     difficulty === 2 ? 'Field Agent' :
                     difficulty === 3 ? 'Specialist' :
                     difficulty === 4 ? 'Special Agent' : 'Master Spy'}
                  </span>
                </div>
              </div>

              <div className={styles.missionActions}>
                <button 
                  className={styles.missionButton}
                  onClick={handleRestartChallenge}
                >
                  Start New Mission
                </button>
              </div>
            </div>
          ) : (
            <MissionDebugProvider>
              <MissionDebugWrapper>
                <CoordinateChallenge 
                  difficulty={difficulty}
                  timeLimit={60}
                  targetScore={10}
                  onComplete={handleChallengeGrade}
                />
              </MissionDebugWrapper>
            </MissionDebugProvider>
          )}
        </div>
      </main>

      <footer className={styles.footer}>
        <div className={styles.difficultySelector}>
          <span className={styles.difficultyLabel}>Set Clearance Level:</span>
          <div className={styles.difficultyControls}>
            {[1, 2, 3, 4, 5].map(level => (
              <button
                key={level}
                className={`${styles.difficultyButton} ${level === difficulty ? styles.active : ''}`}
                onClick={() => !trainingStats.completed && setDifficulty(level)}
                disabled={trainingStats.completed}
              >
                {level}
              </button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TrainingPage; 